import type { BrowserOptions } from '@sentry/angular'
import { sentryInitOptions, type SentryWebEnv } from './sentry-options'

/**
 * FR-075 REQ-5 (AC-5.4) — the web app's Sentry environment comes from the HOST at
 * runtime, not from the build.
 *
 * Production promotes the image staging built (REQ-3, promote-only), so the bundle's
 * baked SENTRY_ENV (`--define` in apps/web/Dockerfile / deploy-staging.yml) is
 * `staging` on production too. The edge serves /runtime-config.js, loaded by
 * index.html before the bundle: on a host that ran scripts/deploy.sh it is the file
 * deploy.sh wrote from the env file (`window.__TIVEDO_RUNTIME__ = {"sentryEnv":…}`);
 * everywhere else it is the committed default (apps/web/public/runtime-config.js), an
 * empty object, so the baked values stand.
 *
 * A runtime value is used only when it is a non-empty string matching the same
 * pattern deploy.sh validates against; anything else is ignored (never thrown on).
 */

// `window.__TIVEDO_RUNTIME__` is typed in src/runtime-config.d.ts.

/** Same pattern as scripts/deploy.sh `SENTRY_ENV_RE`. */
const SENTRY_ENV_RE = /^[a-z][a-z0-9_-]{0,31}$/
/**
 * A Sentry ingest DSN on the hosts the CSP's connect-src allows (docker/Caddyfile:
 * `https://*.ingest.de.sentry.io https://*.ingest.eu.sentry.io`) — the US/global ingest
 * would be blocked by the browser. Same as deploy.sh `SENTRY_DSN_RE`.
 */
const SENTRY_DSN_RE =
  /^https:\/\/[A-Za-z0-9]{1,64}@o[0-9]{1,20}\.ingest\.(de|eu)\.sentry\.io\/[0-9]{1,20}$/

export function isValidSentryEnv(value: unknown): value is string {
  return typeof value === 'string' && SENTRY_ENV_RE.test(value)
}

export function isValidSentryDsnWeb(value: unknown): value is string {
  return typeof value === 'string' && SENTRY_DSN_RE.test(value)
}

/** Pure: the baked env with the valid runtime overrides applied. */
export function resolveSentryWebEnv<T extends SentryWebEnv>(baked: T, runtime: unknown): T {
  if (typeof runtime !== 'object' || runtime === null || Array.isArray(runtime)) return baked
  const { sentryEnv, sentryDsnWeb } = runtime as { sentryEnv?: unknown; sentryDsnWeb?: unknown }
  return {
    ...baked,
    SENTRY_ENV: isValidSentryEnv(sentryEnv) ? sentryEnv : baked.SENTRY_ENV,
    SENTRY_DSN_WEB: isValidSentryDsnWeb(sentryDsnWeb) ? sentryDsnWeb : baked.SENTRY_DSN_WEB,
  }
}

/** The effective env: the baked one resolved against `window.__TIVEDO_RUNTIME__`. */
export function effectiveSentryWebEnv<T extends SentryWebEnv>(baked: T): T {
  const runtime = typeof window === 'undefined' ? undefined : window.__TIVEDO_RUNTIME__
  return resolveSentryWebEnv(baked, runtime)
}

/**
 * The options main.ts passes to `Sentry.init`: the production options (sentry-options.ts)
 * built from the EFFECTIVE env. One function, so a test can build a real client from
 * exactly what main.ts uses.
 */
export function hostSentryInitOptions(baked: SentryWebEnv): BrowserOptions {
  return sentryInitOptions(effectiveSentryWebEnv(baked))
}
